import { useRequest } from 'features/request/useRequest';
import { SortEnumType } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import { useEffect, useMemo } from 'react';

import { catalogUERBaseCostItemsGql } from '../catalogUerBaseCostQueries';

interface IUseCatalogUERBaseData {
    filterValues: Record<string, string[]>;
    id: string | undefined;
}

const createDateRange = (value: string[]) => {
    const [gte, lt] = value.map((date, idx) => {
        if (!date) {
            return undefined;
        }

        const newDate = new Date(date);

        if (idx === 1) {
            newDate.setDate(newDate.getDate() + 1);
        }

        return newDate.toISOString();
    });

    return {
        gte,
        lt,
    };
};

const getWhereVariables = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    const workType = Object.entries(filterValues).reduce((acc: any, [key, value]) => {
        if (!value?.length) {
            return acc;
        }

        if (key === 'workName' || key === 'workInSum') {
            return {
                ...acc,
                [key]: { in: value },
            };
        }

        return acc;
    }, {});

    return {
        ...(Object.keys(workType).length ? { workType } : {}),
        and: Object.entries(filterValues).reduce((acc: any, [key, value]) => {
            if (!value?.length) {
                return acc;
            }

            if (key === 'updatedAt') {
                const updatedAt = createDateRange(value);

                return Object.keys(updatedAt).length
                    ? [
                          ...acc,
                          {
                              updatedAt,
                          },
                      ]
                    : acc;
            }

            if (key !== 'workName' && key !== 'workInSum') {
                return [
                    ...acc,
                    {
                        [key]: { in: value },
                    },
                ];
            }

            return acc;
        }, []),
    };
};

export const useCatalogUERBaseData = ({ filterValues, id }: IUseCatalogUERBaseData) => {
    const [catalogUERBaseResponse, , , catalogUERBaseCostItemsMakeRequest] = useRequest({
        requestId: 'catalogUERBaseCostItems',
        query: catalogUERBaseCostItemsGql,
        withEffect: false,
    });

    const variables = useMemo(
        () =>
            id
                ? {
                      where: getWhereVariables(filterValues),
                      catalogUniqueId: id,
                      order: [{ code: SortEnumType.Asc }],
                  }
                : undefined,
        [filterValues, id],
    );

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'catalogUERBaseCostItems',
        requestFn: catalogUERBaseCostItemsMakeRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (catalogUERBaseResponse?.catalogUERBaseCostItems?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                catalogUERBaseResponse?.catalogUERBaseCostItems?.items ?? [],
                catalogUERBaseResponse?.catalogUERBaseCostItems?.totalCount,
            );
        }
    }, [
        catalogUERBaseResponse?.catalogUERBaseCostItems?.items,
        catalogUERBaseResponse?.catalogUERBaseCostItems?.totalCount,
        getRowParams,
    ]);

    const totalCount = useMemo(
        () => catalogUERBaseResponse?.catalogUERBaseCostItems?.totalCount ?? 0,
        [catalogUERBaseResponse?.catalogUERBaseCostItems?.totalCount],
    );

    const fetchedCount = useMemo<number>(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalCount, getRowParams?.current?.endRow]);

    return useMemo(
        () => ({
            datasource: dataSource,
            totalCount,
            fetchedCount,
        }),
        [dataSource, fetchedCount, totalCount],
    );
};
